import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { MessageSquare, Send, X, Loader2, AlertTriangle } from "lucide-react";

interface Message {
    id: string;
    sender_id: string;
    body: string;
    sender_name: string;
    sender_role: string;
    ai_flagged: boolean;
    created_at: string;
}

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

export function FloatingJobChat({ jobId }: { jobId: string }) {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const jobChannel = useQuery({
        queryKey: ["chat", "job-channel", jobId],
        queryFn: () => api.get<{ channelId: string }>(`/api/chat/job/${jobId}/channel`),
        staleTime: 300_000,
    });

    const channelId = jobChannel.data?.channelId;

    const messages = useQuery({
        queryKey: ["chat", "messages", channelId],
        queryFn: () => api.get<{ messages: Message[] }>(`/api/chat/channels/${channelId}/messages?limit=50`),
        enabled: !!channelId && open,
        refetchInterval: open ? 5000 : false,
    });

    const unread = useQuery({
        queryKey: ["chat", "unread-job", channelId],
        queryFn: () => api.get<{ total: number }>(`/api/chat/unread-total?channelId=${channelId}`),
        enabled: !!channelId,
        refetchInterval: 30000,
    });

    const sendMessage = useMutation({
        mutationFn: async (body: string) => {
            if (!channelId) return;
            return api.post(`/api/chat/channels/${channelId}/messages`, { body });
        },
        onSuccess: () => {
            setText("");
            qc.invalidateQueries({ queryKey: ["chat", "messages", channelId] });
            qc.invalidateQueries({ queryKey: ["chat", "unread-job", channelId] });
        },
    });

    const handleSend = useCallback(() => {
        const trimmed = text.trim();
        if (!trimmed) return;
        sendMessage.mutate(trimmed);
    }, [text, sendMessage]);

    // SSE listener for real-time messages
    useEffect(() => {
        if (!open || !channelId) return;
        const token = getToken();
        if (!token) return;
        const es = new EventSource(`/api/sse?token=${encodeURIComponent(token)}`);
        es.addEventListener("chat:new_message", (e) => {
            try {
                const data = JSON.parse(e.data);
                if (data.channel_id === channelId) {
                    qc.invalidateQueries({ queryKey: ["chat", "messages", channelId] });
                }
            } catch {}
        });
        es.onerror = () => es.close();
        return () => es.close();
    }, [open, channelId, qc]);

    // Auto-scroll on new messages
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.data?.messages?.length]);

    // Focus input when opened
    useEffect(() => {
        if (open && channelId) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [open, channelId]);

    const unreadCount = unread.data?.total ?? 0;

    return (
        <>
            {/* Floating button */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                    aria-label="Open chat"
                >
                    <MessageSquare className="h-6 w-6" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                            {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                    )}
                </button>
            )}

            {/* Bottom sheet */}
            {open && (
                <div className="fixed inset-0 z-50 flex flex-col justify-end">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
                    {/* Sheet */}
                    <div className="relative bg-card border-t rounded-t-2xl shadow-2xl flex flex-col h-[70vh] max-h-[600px] animate-in slide-in-from-bottom duration-300">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                            <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-primary" />
                                <span className="font-semibold text-sm">Job Chat</span>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                            {(messages.isLoading || jobChannel.isLoading) && (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-5 w-5 animate-spin text-foreground/80" />
                                </div>
                            )}
                            {messages.data?.messages?.length === 0 && !messages.isLoading && (
                                <p className="text-center text-sm text-foreground/80 py-8">
                                    No messages yet. Send a message to the office.
                                </p>
                            )}
                            {messages.data?.messages?.map((msg) => {
                                const isMe = msg.sender_id === user?.id;
                                return (
                                    <div
                                        key={msg.id}
                                        className={cn(
                                            "flex flex-col max-w-[85%]",
                                            isMe ? "ml-auto items-end" : "items-start",
                                        )}
                                    >
                                        {!isMe && (
                                            <span className="text-[10px] text-foreground/80 mb-0.5 px-1">
                                                {msg.sender_name}
                                                {msg.sender_role && ` · ${msg.sender_role}`}
                                            </span>
                                        )}
                                        <div
                                            className={cn(
                                                "rounded-2xl px-3 py-2 text-sm break-words",
                                                isMe
                                                    ? "bg-primary text-primary-foreground"
                                                    : "bg-secondary text-secondary-foreground",
                                                msg.ai_flagged && "ring-2 ring-red-400",
                                            )}
                                        >
                                            {msg.body}
                                            {msg.ai_flagged && (
                                                <AlertTriangle className="h-3 w-3 inline-block ml-1 text-red-400" />
                                            )}
                                        </div>
                                        <span className="text-[10px] text-foreground/80 mt-0.5 px-1">
                                            {relativeTime(msg.created_at)}
                                        </span>
                                    </div>
                                );
                            })}
                            <div ref={bottomRef} />
                        </div>

                        {/* Input */}
                        <div className="border-t p-3 flex gap-2 shrink-0">
                            <Input
                                ref={inputRef}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder="Message the office..."
                                className="flex-1"
                            />
                            <Button
                                size="icon"
                                className="h-10 w-10 shrink-0"
                                onClick={handleSend}
                                disabled={sendMessage.isPending || !text.trim()}
                            >
                                {sendMessage.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
